// ----------------------------------------------------------------------
// <copyright file="Izenda.SubscribedInstances.js" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
//  </copyright>
// ----------------------------------------------------------------------

!(function() {
  var m = require('mobx');
  var PagingModel = require('share/components/controls/DataGrid/helpers/Izenda.PagingModel');
  var ObjectStateConstants = require('logic/data/Izenda.ObjectStateConstants');
  var constant = require('logic/data/Izenda.ReportSubscriptionConstants');
  var DEFAULT_VALUE = constant.DEFAULT_VALUE;
  var trackable = require('share/tracking/Izenda.Trackable');
  var SubscriptionDetails = require('logic/models/Izenda.SubscriptionDetails');
  var MobxUtils = require('share/utils/Izenda.MobxUtils');

  const {
    filter,
    isEmpty,
    every,
    some,
    forEach,
    isNil,
    isEqual,
    map,
    cloneDeep,
    uniqueId,
    findIndex,
    indexOf,
    get,
    find,
    placeholder
  } = require('lodash/fp');

  function SubscribedInstances(data) {
    var props = {
      pageInfo: new PagingModel(DEFAULT_VALUE.SORT_DIRS, DEFAULT_VALUE.SEARCH_CRITERIA),
      subscribedInstances: [], // array of SubscriptionDetails.
      emailTemplates: null,
      isSubscriptionMode: true,
      scheduleInstances: [], // array of SubscriptionDetails that is used for report designer schedule.

      activeInstances: function() {
        return filter(function(item) {
          return item.state !== ObjectStateConstants.DELETE;
        }, this.subscribedInstances);
      },

      hasAllActiveInstancesSelected: function() {
        if (isEmpty(this.activeInstances)) {
          return false;
        }
        return every(['selectedValue', true], this.activeInstances);
      },

      hasActiveInstancesSelected: function() {
        if (isEmpty(this.activeInstances)) {
          return false;
        }
        return some(['selectedValue', true], this.activeInstances);
      }
    };

    m.extendObservable(trackable(this), props);
    this.cleanDirty();
  }

  /**
   * Populate data
   * @param  {object} data
   */
  SubscribedInstances.prototype.populateData = m.action(function(data) {
    data = data || {};
    this.subscribedInstances = [];

    forEach(
      function(item) {
        this.subscribedInstances.push(new SubscriptionDetails(item));
      }.bind(this),
      data.result
    );

    this.pageInfo.setTotalRecords(data.total);

    this.cleanDirty();
    return Promise.resolve({});
  });

  /**
   * Load active instance
   */
  SubscribedInstances.prototype.loadActiveInstances = function() {
    return filter(function(item) {
      return item.state !== ObjectStateConstants.DELETE;
    }, this.subscribedInstances);
  };

  /**
   * Load email templates
   * @param {object} data
   */
  SubscribedInstances.prototype.populateEmailTemplates = function(data) {
    this.emailTemplates = data;
    this.markDirty();
    return Promise.resolve({});
  };

  /**
   * Check dirty status
   * @return {boolean} dirty status
   */
  SubscribedInstances.prototype.checkDirty = function() {
    var dirtyItems = filter(function(item) {
      return item.checkDirty && item.checkDirty();
    }, this.subscribedInstances);

    return !isEmpty(dirtyItems) && this.isDirty;
  };

  /**
   * Clean dirty status.
   */
  SubscribedInstances.prototype.cleanDirty = function() {
    forEach(function(item) {
      item.cleanDirty && item.cleanDirty();
    }, this.subscribedInstances);

    this.resetDirty();
  };

  /**
   * Copy a subscribed instance and add it at first of subscribed instances list.
   * @param {String} id
   */
  SubscribedInstances.prototype.copySubscribedInstance = function(id) {
    this.copy(this.subscribedInstances, id);
  };

  /**
   * Copy a schedule instance and add it at first of subscribed instances list.
   * @param {String} id
   */
  SubscribedInstances.prototype.copyScheduleInstances = m.action(function(id) {
    this.scheduleInstances = this.subscribedInstances;
    this.markDirty();
  });

  /**
   * Copy a instance.
   * @param {String} id
   */
  SubscribedInstances.prototype.copy = m.action(function(instances, id) {
    var findInstance = function(id) {
      return find(function(item) {
        return item.id === id;
      }, instances);
    };

    var updateFilters = m.action(function(filters) {
      forEach(function(item) {
        item.state = ObjectStateConstants.INSERT;
        item.id = null;
        item.subscriptionId = null;
      }, filters);
    });

    var copyInstance = m.action(function(instance) {
      var clonedInstance = new SubscriptionDetails(cloneDeep(matchedInstance));
      var newName = String.generateNewName(clonedInstance.name, instances, get('name'));

      clonedInstance.updateProperty('id', uniqueId('newInstance_'));
      clonedInstance.updateProperty('name', newName);
      clonedInstance.updateProperty('state', ObjectStateConstants.INSERT);
      clonedInstance.updateProperty(
        'subscriptionFilterFields',
        matchedInstance.subscriptionFilterFields && cloneDeep(MobxUtils.toJS(matchedInstance.subscriptionFilterFields))
      );
      clonedInstance.updateProperty(
        'subscriptionCommonFilterFields',
        matchedInstance.subscriptionCommonFilterFields &&
          cloneDeep(MobxUtils.toJS(matchedInstance.subscriptionCommonFilterFields))
      );

      updateFilters(clonedInstance.subscriptionFilterFields);
      updateFilters(clonedInstance.subscriptionCommonFilterFields);

      return clonedInstance;
    });

    var matchedInstance = findInstance(id);
    if (!matchedInstance) {
      return;
    }

    var newInstance = copyInstance(matchedInstance);
    instances.splice(0, 0, newInstance);
    this.pageInfo.increaseRecord();
    this.markDirty();
  });

  /**
   * Compose list of instances for saving.
   * @returns {Array}
   */
  SubscribedInstances.prototype.buildDataForSaving = function() {
    var subscribedInstances = placeholder(this.subscribedInstances)
      .chain()
      .filter(function(item) {
        return item.state !== ObjectStateConstants.NONE;
      })
      .forEach(function(i) {
        if (i.state === ObjectStateConstants.INSERT) {
          i.id = null;
        }
      })
      .value();

    return subscribedInstances;
  };

  /**
   * Delete subscribed instance.
   * Just remove in browser, user must save for real delete.
   * @param {Array} ids
   */
  SubscribedInstances.prototype.removeSubscribedInstances = function(ids) {
    this.delete(this.subscribedInstances, ids);
  };

  /**
   * Delete schedule instance.
   * Just remove in browser, user must save for real delete.
   * @param {Array} ids
   */
  SubscribedInstances.prototype.removeScheduleInstances = function(ids) {
    this.delete(this.scheduleInstances, ids);
  };

  /**
   * Delete instance.
   * Just remove in browser, user must save for real delete.
   * @param {Array} ids
   */
  SubscribedInstances.prototype.delete = m.action(function(instances, ids) {
    var pageInfo = this.pageInfo;
    forEach(function(id) {
      var index = findIndex(function(item) {
        return item.id === id;
      }, instances);

      if (index !== -1) {
        switch (instances[index].state) {
          case ObjectStateConstants.INSERT:
            instances.splice(index, 1);
            break;
          default:
            instances[index].updateProperty('state', ObjectStateConstants.DELETE);
            instances[index].updateSelectedValue(false);
            forEach(function(item) {
              item.state = ObjectStateConstants.DELETE;
            }, instances[index].subscriptionFilterFields);
            break;
        }

        pageInfo.descreaseRecord();
      }
    }, ids);

    this.markDirty();
  });

  /**
   * Update subscribed instance.
   * @param {Object} subscription data
   */
  SubscribedInstances.prototype.updateSubscribedInstances = m.action(function(subData) {
    var subscription = find(function(s) {
      return isEqual(s.id, subData.id);
    }, this.subscribedInstances);

    if (isNil(subscription)) {
      this.subscribedInstances.push(subData);
      this.pageInfo.increaseRecord();
    } else {
      var i = indexOf(subscription, this.subscribedInstances);
      this.subscribedInstances[i] = subData;
    }

    this.markDirty();
  });

  /**
   * Check duplicate instance name.
   * Just check with active instances.
   * @param {Object} instance
   * @returns {Boolean}
   */
  SubscribedInstances.prototype.checkDuplicateInstanceName = function(instance) {
    var matchedInstance = find(function(item) {
      return item.name.toUpperCase() === instance.name.toUpperCase() && item.id !== instance.id;
    }, this.loadActiveInstances());

    return !!matchedInstance;
  };

  /**
   * Set type mode: Subscription or Schedule
   * @param {Boolean} Subscription or Schedule
   */
  SubscribedInstances.prototype.setType = function(isSubscriptionMode) {
    this.isSubscriptionMode = isSubscriptionMode;
  };

  /**
   * Update data for schedule instances
   * @param {Boolean} isNew
   * @param {Object} data
   */
  SubscribedInstances.prototype.updateScheduleInstances = m.action(function(isNew, data) {
    if (isNew) {
      this.scheduleInstances = map(function(item) {
        return new SubscriptionDetails(item);
      }, this.subscribedInstances);
    } else {
      if (!isNil(data)) {
        var subscription = find(function(s) {
          return isEqual(s.id, data.id);
        }, this.scheduleInstances);

        if (isNil(subscription)) {
          this.scheduleInstances.push(data);
        } else {
          var i = indexOf(subscription, this.scheduleInstances);
          this.scheduleInstances[i] = data;
        }
      }
    }
    this.markDirty();
  });

  /**
   * Reset page model
   */
  SubscribedInstances.prototype.resetPageModel = function() {
    this.pageInfo = new PagingModel(DEFAULT_VALUE.SORT_DIRS, DEFAULT_VALUE.SEARCH_CRITERIA);
  };

  /**
   * Update selected all subscription item
   * @param {boolean} value
   */
  SubscribedInstances.prototype.selectAll = m.action(function(value) {
    forEach(function(item) {
      item.updateSelectedValue(value);
    }, this.activeInstances);
  });

  module.exports = SubscribedInstances;
})();



// WEBPACK FOOTER //
// ./ui/logic/models/Izenda.SubscribedInstances.js